import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import './DiscountsPage.css';

const API_BASE = process.env.REACT_APP_API_BASE_URL || '';
// Firebase Hosting rewrites (the normal /api/** path) hard-cap requests at ~60s. A full discount
// sync can run longer than that under Shopify API throttling, so the sync button calls the
// function's direct Cloud Run URL instead, which honors the function's own (540s) timeout.
const DISCOUNT_SYNC_DIRECT_URL = 'https://app-frgo5nxgza-uc.a.run.app/api/v1/admin/discounts/sync';

function formatDateTime(value) {
  if (!value) return '—';
  const ms = Date.parse(value);
  if (!Number.isFinite(ms)) return '—';
  return new Date(ms).toLocaleString();
}

function formatTypeValue(discount) {
  if (discount.type === 'percentage') return `${discount.value}% off`;
  if (discount.type === 'fixed_amount') return `₹${discount.value} off`;
  if (discount.type === 'free_shipping') return 'Free shipping';
  return discount.type;
}

function formatMinRequirement(discount) {
  if (discount.minOrder) return `Min ₹${discount.minOrder}`;
  if (discount.minOrderQty) return `Min qty ${discount.minOrderQty}`;
  return 'No minimum';
}

function formatUsage(discount) {
  const used = discount.usageCount ?? 0;
  const limit = discount.usageLimit;
  return `${used} / ${limit == null ? 'Unlimited' : limit}`;
}

function autoDescription(discount) {
  if (discount.type === 'percentage') {
    return discount.minOrder
      ? `Get ${discount.value}% off on minimum order of ₹${discount.minOrder}`
      : `Get ${discount.value}% off`;
  }
  if (discount.type === 'fixed_amount') {
    return discount.minOrder
      ? `Get ₹${discount.value} off on minimum order of ₹${discount.minOrder}`
      : `Get ₹${discount.value} off`;
  }
  return 'Free shipping on your order';
}

const TAG_OPTIONS = [
  { value: '', label: 'Auto (best value / popular / new)' },
  { value: 'best_value', label: 'Best Value' },
  { value: 'popular', label: 'Popular' },
  { value: 'new', label: 'New' },
  { value: 'none', label: 'None (never show a tag)' },
];

const TAG_PILL_LABEL = {
  best_value: 'BEST VALUE',
  popular: 'POPULAR',
  new: 'NEW',
};

const AUDIENCE_OPTIONS = [
  { value: 'all', label: 'All Customers' },
  { value: 'wholesale', label: 'Wholesale Only' },
  { value: 'retail', label: 'Retail Only (hidden from wholesale)' },
];

function EditDiscountModal({ discount, onClose, onSave, saving }) {
  const [form, setForm] = useState({
    display_label: discount.display_label || '',
    display_description: discount.display_description || '',
    badge_text: discount.badge_text || '',
    audience: discount.audience || 'all',
    show_on_cart: Boolean(discount.show_on_cart),
  });

  const previewLabel = form.display_label.trim() || discount.code;
  const previewDescription = form.display_description.trim() || autoDescription(discount);

  return (
    <div className="discounts-modal-overlay" onClick={onClose}>
      <div className="discounts-modal" onClick={(e) => e.stopPropagation()}>
        <h2>Edit {discount.code}</h2>

        <label className="discounts-modal-field">
          Display Label
          <input
            type="text"
            placeholder={discount.code}
            value={form.display_label}
            onChange={(e) => setForm((f) => ({ ...f, display_label: e.target.value }))}
          />
        </label>

        <label className="discounts-modal-field">
          Display Description
          <input
            type="text"
            placeholder={autoDescription(discount)}
            value={form.display_description}
            onChange={(e) => setForm((f) => ({ ...f, display_description: e.target.value }))}
          />
        </label>

        <label className="discounts-modal-field">
          Tag
          <select
            value={form.badge_text}
            onChange={(e) => setForm((f) => ({ ...f, badge_text: e.target.value }))}
          >
            {TAG_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </label>

        <label className="discounts-modal-field">
          Audience
          <select
            value={form.audience}
            onChange={(e) => setForm((f) => ({ ...f, audience: e.target.value }))}
          >
            {AUDIENCE_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </label>

        <label className="discounts-modal-checkbox">
          <input
            type="checkbox"
            checked={form.show_on_cart}
            onChange={(e) => setForm((f) => ({ ...f, show_on_cart: e.target.checked }))}
          />
          Show on Cart
        </label>

        <div className="discounts-preview">
          <span className="discounts-preview-label">Cart preview</span>
          <div className="discounts-preview-card">
            {TAG_PILL_LABEL[form.badge_text] && (
              <span className="discounts-preview-badge">{TAG_PILL_LABEL[form.badge_text]}</span>
            )}
            {!form.badge_text && <span className="discounts-preview-auto-tag">tag: auto-computed</span>}
            <strong>{previewLabel}</strong>
            <p>{previewDescription}</p>
          </div>
        </div>

        <div className="discounts-modal-actions">
          <button className="discounts-btn discounts-btn-secondary" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="discounts-btn discounts-btn-primary" onClick={() => onSave(form)} disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function DiscountsPage() {
  const { currentUser } = useAuth();

  const [discounts, setDiscounts] = useState([]);
  const [lastSyncedAt, setLastSyncedAt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [message, setMessage] = useState(null);

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showOnCartFilter, setShowOnCartFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');

  const [selectedCodes, setSelectedCodes] = useState(new Set());
  const [editingDiscount, setEditingDiscount] = useState(null);
  const [savingEdit, setSavingEdit] = useState(false);
  const [bulkRunning, setBulkRunning] = useState(false);

  const authedFetch = useCallback(async (url, options = {}) => {
    const token = await currentUser.getIdToken();
    const resolvedUrl = url.startsWith('http') ? url : `${API_BASE}${url}`;
    return fetch(resolvedUrl, {
      ...options,
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
        ...(options.headers || {}),
      },
    });
  }, [currentUser]);

  const loadDiscounts = useCallback(async () => {
    setLoading(true);
    try {
      const res = await authedFetch('/api/v1/admin/discounts');
      const data = await res.json();
      if (!data.success) throw new Error(data.message || 'Failed to load discounts.');
      setDiscounts(data.discounts || []);
      setLastSyncedAt(data.lastSyncedAt || null);
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    } finally {
      setLoading(false);
    }
  }, [authedFetch]);

  useEffect(() => {
    loadDiscounts();
  }, [loadDiscounts]);

  const handleSync = useCallback(async () => {
    setSyncing(true);
    setMessage(null);
    try {
      const res = await authedFetch(DISCOUNT_SYNC_DIRECT_URL, { method: 'POST' });
      const data = await res.json();
      if (!data.success) throw new Error(data.message || 'Sync failed.');
      const s = data.summary || {};
      setMessage({ type: 'success', text: `Synced ${s.fetched} discounts — ${s.created} new, ${s.updated} updated, ${s.removed} removed.` });
      await loadDiscounts();
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    } finally {
      setSyncing(false);
    }
  }, [authedFetch, loadDiscounts]);

  const toggleShowOnCart = useCallback(async (discount) => {
    try {
      const res = await authedFetch(`/api/v1/admin/discounts/${encodeURIComponent(discount.code)}`, {
        method: 'PATCH',
        body: JSON.stringify({ show_on_cart: !discount.show_on_cart }),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.message || 'Update failed.');
      setDiscounts((prev) => prev.map((d) => (d.code === discount.code ? { ...d, show_on_cart: !discount.show_on_cart } : d)));
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    }
  }, [authedFetch]);

  const toggleWholesaleOnly = useCallback(async (discount) => {
    const nextAudience = discount.audience === 'wholesale' ? 'all' : 'wholesale';
    try {
      const res = await authedFetch(`/api/v1/admin/discounts/${encodeURIComponent(discount.code)}`, {
        method: 'PATCH',
        body: JSON.stringify({ audience: nextAudience }),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.message || 'Update failed.');
      setDiscounts((prev) => prev.map((d) => (d.code === discount.code ? { ...d, audience: nextAudience } : d)));
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    }
  }, [authedFetch]);

  const saveEdit = useCallback(async (form) => {
    if (!editingDiscount) return;
    setSavingEdit(true);
    try {
      const res = await authedFetch(`/api/v1/admin/discounts/${encodeURIComponent(editingDiscount.code)}`, {
        method: 'PATCH',
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.message || 'Save failed.');
      setDiscounts((prev) => prev.map((d) => (d.code === editingDiscount.code ? { ...d, ...form } : d)));
      setEditingDiscount(null);
      setMessage({ type: 'success', text: `Saved ${editingDiscount.code}.` });
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    } finally {
      setSavingEdit(false);
    }
  }, [authedFetch, editingDiscount]);

  const toggleSelected = useCallback((code) => {
    setSelectedCodes((prev) => {
      const next = new Set(prev);
      if (next.has(code)) next.delete(code);
      else next.add(code);
      return next;
    });
  }, []);

  const runBulkToggle = useCallback(async (showOnCart) => {
    if (selectedCodes.size === 0) return;
    setBulkRunning(true);
    try {
      const res = await authedFetch('/api/v1/admin/discounts/bulk-toggle', {
        method: 'POST',
        body: JSON.stringify({ codes: [...selectedCodes], show_on_cart: showOnCart }),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.message || 'Bulk update failed.');
      setDiscounts((prev) => prev.map((d) => (selectedCodes.has(d.code) ? { ...d, show_on_cart: showOnCart } : d)));
      setSelectedCodes(new Set());
      setMessage({ type: 'success', text: `Updated ${selectedCodes.size} discounts.` });
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    } finally {
      setBulkRunning(false);
    }
  }, [authedFetch, selectedCodes]);

  const filteredDiscounts = useMemo(() => {
    const term = search.trim().toUpperCase();
    return discounts.filter((d) => {
      if (term && !String(d.code || '').toUpperCase().includes(term)) return false;
      if (statusFilter !== 'all' && d.status !== statusFilter) return false;
      if (showOnCartFilter !== 'all') {
        const want = showOnCartFilter === 'yes';
        if (Boolean(d.show_on_cart) !== want) return false;
      }
      if (typeFilter !== 'all' && d.type !== typeFilter) return false;
      return true;
    });
  }, [discounts, search, statusFilter, showOnCartFilter, typeFilter]);

  if (loading) {
    return <div className="discounts-page"><div className="discounts-loading">Loading discounts...</div></div>;
  }

  return (
    <div className="discounts-page">
      <div className="discounts-header">
        <div>
          <h1>Discount Management</h1>
          <p>Sync Shopify discount codes and control which ones appear on the storefront cart.</p>
        </div>
        <div className="discounts-header-actions">
          <button className="discounts-btn discounts-btn-primary" onClick={handleSync} disabled={syncing}>
            {syncing ? 'Syncing…' : 'Sync from Shopify'}
          </button>
          <span className="discounts-last-sync">Last synced: <strong>{formatDateTime(lastSyncedAt)}</strong></span>
        </div>
      </div>

      {message ? <div className={`discounts-alert discounts-alert-${message.type}`}>{message.text}</div> : null}

      <div className="discounts-toolbar">
        <input
          className="discounts-input discounts-input-wide"
          placeholder="Search by code..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <select className="discounts-select" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
          <option value="all">All statuses</option>
          <option value="active">Active</option>
          <option value="expired">Expired</option>
          <option value="scheduled">Scheduled</option>
          <option value="removed">Removed</option>
        </select>
        <select className="discounts-select" value={showOnCartFilter} onChange={(e) => setShowOnCartFilter(e.target.value)}>
          <option value="all">Show on Cart: All</option>
          <option value="yes">Show on Cart: Yes</option>
          <option value="no">Show on Cart: No</option>
        </select>
        <select className="discounts-select" value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
          <option value="all">All types</option>
          <option value="percentage">Percentage</option>
          <option value="fixed_amount">Fixed Amount</option>
          <option value="free_shipping">Free Shipping</option>
        </select>
      </div>

      {selectedCodes.size > 0 && (
        <div className="discounts-bulk-bar">
          <span>{selectedCodes.size} selected</span>
          <button className="discounts-btn discounts-btn-secondary" onClick={() => runBulkToggle(true)} disabled={bulkRunning}>
            Enable Show on Cart
          </button>
          <button className="discounts-btn discounts-btn-secondary" onClick={() => runBulkToggle(false)} disabled={bulkRunning}>
            Disable Show on Cart
          </button>
        </div>
      )}

      <div className="discounts-table-wrap">
        <table className="discounts-table">
          <thead>
            <tr>
              <th></th>
              <th>Code</th>
              <th>Type &amp; Value</th>
              <th>Min Requirement</th>
              <th>Usage</th>
              <th>Expiry</th>
              <th>Show on Cart</th>
              <th>Wholesale Only</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {filteredDiscounts.map((d) => (
              <tr key={d.code}>
                <td>
                  <input
                    type="checkbox"
                    checked={selectedCodes.has(d.code)}
                    onChange={() => toggleSelected(d.code)}
                  />
                </td>
                <td>
                  <strong>{d.code}</strong>
                  <div><span className={`discounts-status-badge discounts-status-${d.status}`}>{d.status}</span></div>
                </td>
                <td>{formatTypeValue(d)}</td>
                <td>{formatMinRequirement(d)}</td>
                <td>{formatUsage(d)}</td>
                <td>{d.endsAt ? formatDateTime(d.endsAt) : 'No expiry'}</td>
                <td>
                  <input
                    type="checkbox"
                    checked={Boolean(d.show_on_cart)}
                    onChange={() => toggleShowOnCart(d)}
                  />
                </td>
                <td>
                  <input
                    type="checkbox"
                    checked={d.audience === 'wholesale'}
                    onChange={() => toggleWholesaleOnly(d)}
                    title={d.audience === 'retail' ? 'Currently set to Retail Only — checking this switches it to Wholesale Only' : ''}
                  />
                  {d.audience === 'retail' && <div className="discounts-audience-hint">(Retail only)</div>}
                </td>
                <td>
                  <button className="discounts-btn discounts-btn-secondary" onClick={() => setEditingDiscount(d)}>
                    Edit
                  </button>
                </td>
              </tr>
            ))}
            {!filteredDiscounts.length ? (
              <tr><td colSpan="9" className="discounts-empty-cell">No discounts match the current filters.</td></tr>
            ) : null}
          </tbody>
        </table>
      </div>

      {editingDiscount ? (
        <EditDiscountModal
          discount={editingDiscount}
          onClose={() => setEditingDiscount(null)}
          onSave={saveEdit}
          saving={savingEdit}
        />
      ) : null}
    </div>
  );
}
